import React, {
  createContext,
  useContext,
  ReactNode,
  useState,
  useMemo,
  useEffect,
} from 'react';

import { usePathname } from 'next/navigation';

import { HideDesktop } from '@repo/editor/src/components/utilities/queries';

import { FlexRow } from 'styles/layouts';

import { SidebarLayoutWrapper } from './SidebarLayoutWrapper';
import { SideBarBackdrop } from './styles';

/**
 * Context type definition for the sidebar state and controls
 */
type SidebarContextType = {
  /** Whether the sidebar is currently rendered in the DOM */
  isSidebarRendered: boolean;
  /** Whether the sidebar is currently visible/opened */
  isSidebarOpened: boolean;
  /** Function to control sidebar rendering */
  setIsSidebarRendered: (show: boolean) => void;
  /** Function to control sidebar visibility */
  setIsSidebarOpened: (show: boolean) => void;
  /** The optimistic path that will be navigated to */
  optimisticPath: string;
  /** Function to update the optimistic path */
  setOptimisticPath: (path: string) => void;
};

/**
 * Context instance with default values for the sidebar
 */
const SidebarContext = createContext<SidebarContextType>({
  isSidebarRendered: false,
  isSidebarOpened: false,
  setIsSidebarRendered: () => {},
  setIsSidebarOpened: () => {},
  optimisticPath: '',
  setOptimisticPath: () => {},
});

/**
 * Provider component that manages sidebar state and layout
 *
 * Handles:
 * - Sidebar rendering state
 * - Sidebar visibility state
 * - Mobile backdrop
 * - Layout structure based on sidebar state
 * - Optimistic path updates for menu animations
 */
export const SidebarProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  const pathname = usePathname();
  const [isSidebarRendered, setIsSidebarRendered] = useState(false);
  const [isSidebarOpened, setIsSidebarOpened] = useState(false);
  const [optimisticPath, setOptimisticPath] = useState(pathname || '');

  // Sync optimistic path with actual path & close sidebar on route changes
  useEffect(() => {
    setOptimisticPath(pathname || '');
  }, [pathname]);

  useEffect(() => {
    setIsSidebarOpened(false);
  }, [optimisticPath]);

  const value = useMemo(
    () => ({
      isSidebarRendered,
      setIsSidebarRendered,
      isSidebarOpened,
      setIsSidebarOpened,
      optimisticPath,
      setOptimisticPath,
    }),
    [isSidebarRendered, isSidebarOpened, optimisticPath]
  );

  return (
    <SidebarContext.Provider value={value}>
      {isSidebarRendered ? (
        <FlexRow>
          <SidebarLayoutWrapper />
          {children}
        </FlexRow>
      ) : (
        children
      )}
      <HideDesktop>
        {isSidebarOpened && (
          <SideBarBackdrop onClick={() => setIsSidebarOpened(false)} />
        )}
      </HideDesktop>
    </SidebarContext.Provider>
  );
};

/**
 * Hook to access sidebar context values from any child component
 *
 * @returns {SidebarContextType} Object containing sidebar state and control functions
 *
 * @example
 * const { isSidebarOpened, setIsSidebarOpened } = useSidebar();
 */
export const useSidebar = () => useContext(SidebarContext);
